import React, { useEffect, useState } from 'react'
import Image from 'next/legacy/image'
import styled from 'styled-components'
import toast from 'react-hot-toast'
import { Column, Row } from 'components/common/layout'
import { H4Text, SmallText } from 'components/common/text'
import { FloatingButton } from 'components/scouts/common/styles'
import { useUser } from 'apis/hooks'
import { Modal } from 'components/common/modal'
import { TextInput } from 'components/common/input'
import { putAccountsMe } from 'apis/request'
import { VALID_EMAIL } from 'infra/constants'
import { mutate } from 'swr'
import { useStores } from 'stores/globals'

const DEPLOYED_AT = '2025-08-17 19:30'

export const EmailConfirmModalContainer: React.FC = () => {
  const { data: user } = useUser()
  const { loginStore } = useStores()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (!user || isLoaded) return
    if (
      !user.updatedByUserAt ||
      new Date(DEPLOYED_AT) > new Date(user.updatedByUserAt)
    ) {
      setIsOpen(true)
    }
    setIsLoaded(true)
  }, [user, isLoaded])

  if (!isOpen || !user || loginStore.isFake) return null
  return (
    <EmailConfirmModal
      defaultValue={user.email}
      onClose={() => setIsOpen(false)}
    />
  )
}

export const EmailConfirmModal: React.FC<{
  defaultValue: string
  onClose: () => void
}> = ({ defaultValue, onClose }) => {
  const [email, setEmail] = useState(defaultValue)
  const [touched, setTouched] = useState(false)
  const isValid = VALID_EMAIL.test(email)
  const isChanged = email !== defaultValue
  return (
    <Modal width={400} onClose={onClose} disableCloseBtn disableBackgroundClick>
      <ModalContainer>
        <Title>이메일 주소를 확인해주세요</Title>
        <DescriptionBox>
          <Description>
            중요한 내용을 놓치지 않도록,
            <br />
            사용중인 메일 주소로 변경해주세요!
          </Description>
        </DescriptionBox>
        <Column style={{ gap: 8 }}>
          <InputRow>
            <Image src='/mail.svg' width={24} height={24} alt='mail' />
            <TextInput
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setTouched(true)
              }}
              placeholder='이메일 입력'
              autoFocus
            />
          </InputRow>
          {touched && !isValid && (
            <ErrorText>올바른 이메일 형식을 입력해주세요.</ErrorText>
          )}
        </Column>
        <ConfirmButton
          onClick={() => {
            if (isChanged && isValid) {
              toast.success('이메일이 변경되었어요.')
            }
            putAccountsMe({ email }).then(() => mutate('/accounts/me'))
            onClose()
          }}
          disabled={!isValid}
        >
          {isChanged ? '이메일 변경 완료하기' : '사용중인 이메일이 맞아요'}
        </ConfirmButton>
      </ModalContainer>
    </Modal>
  )
}

const ErrorText = styled(SmallText)`
  color: ${(p) => p.theme.color.red};
  margin-top: 4px;
  text-align: left;
  width: 100%;
`

const ModalContainer = styled(Column)`
  gap: 32px;
  padding: 40px 32px 32px;
`

const Title = styled(H4Text)`
  color: ${(p) => p.theme.color.black2};
  font-weight: ${(p) => p.theme.fontWeight.semiBold};
  text-align: center;
`

const DescriptionBox = styled.div`
  width: 100%;
  background: ${(p) => p.theme.color.background};
  border-radius: 8px;
  padding: 20px 0;
  display: flex;
  justify-content: center;
`

const Description = styled(SmallText)`
  color: ${(p) => p.theme.color.black2};
  text-align: center;
  line-height: 1.6;
`

const InputRow = styled(Row)`
  gap: 12px;
`

const ConfirmButton = styled(FloatingButton)`
  width: 100%;
  margin-top: 16px;
  background: ${(p) => p.theme.color.primary};
  color: #fff;
  font-weight: ${(p) => p.theme.fontWeight.semiBold};
  font-size: 16px;
  border-radius: 8px;
  box-shadow: none;
  &:hover {
    background: ${(p) => p.theme.color.primary};
  }
`
